// 環境設定管理

export type Environment = 'local' | 'fkdev' | 'prod';

export interface AppConfig {
  env: Environment;
  apiBaseUrl: string;
  oidc: {
    authority: string;
    clientId: string;
    scope: string;
  };
}

export function getAppConfig(): AppConfig {
  const env = (import.meta.env.VITE_APP_ENV as Environment) || 'local';

  // デバッグ情報の出力（開発環境およびfkdev環境のみ）
  if (import.meta.env.MODE === 'development' || env === 'fkdev') {
    console.log('🔧 環境設定デバッグ情報:', {
      MODE: import.meta.env.MODE,
      VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
      env,
      VITE_BACKEND_API_URL: import.meta.env.VITE_BACKEND_API_URL,
      VITE_OIDC_AUTHORITY: import.meta.env.VITE_OIDC_AUTHORITY,
      VITE_OIDC_CLIENT_ID: import.meta.env.VITE_OIDC_CLIENT_ID,
    });
  }

  // 環境変数から設定を取得
  const getOidcConfig = () => {
    // 環境変数の優先順位: VITE_* > デフォルト値
    const authority = import.meta.env.VITE_OIDC_AUTHORITY || '';
    const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || '';
    const scope = import.meta.env.VITE_OIDC_SCOPE || 'email openid phone';

    return {
      authority,
      clientId,
      scope,
    };
  };

  const getApiBaseUrl = () => {
    // 環境変数から取得、デフォルトは環境に応じて設定
    const apiBaseUrl = import.meta.env.VITE_BACKEND_API_URL;

    if (apiBaseUrl) {
      return apiBaseUrl;
    }

    // デフォルト値
    switch (env) {
      case 'local':
        return 'http://localhost:8080/api/v1';
      case 'fkdev':
      case 'prod':
      default:
        return '/backend-api/api/v1';
    }
  };

  const config = {
    env,
    apiBaseUrl: getApiBaseUrl(),
    oidc: getOidcConfig(),
  };

  // 必須項目の検証
  if (!config.oidc.authority || !config.oidc.clientId) {
    console.warn(`⚠️ 環境変数が不足しています: ${env}`);
    console.warn(`   VITE_OIDC_AUTHORITY: ${config.oidc.authority ? '設定済み' : '未設定'}`);
    console.warn(`   VITE_OIDC_CLIENT_ID: ${config.oidc.clientId ? '設定済み' : '未設定'}`);
  }

  // 開発環境およびfkdev環境では設定内容をログ出力
  if (import.meta.env.MODE === 'development' || env === 'fkdev') {
    console.log('📋 最終設定:', {
      env: config.env,
      apiBaseUrl: config.apiBaseUrl,
      oidc: {
        authority: config.oidc.authority,
        clientId: config.oidc.clientId,
        scope: config.oidc.scope,
      },
    });
  }

  return config;
}

// 設定の妥当性をチェック
export function validateConfig(config: AppConfig): string[] {
  const errors: string[] = [];

  if (!config.oidc.authority) {
    errors.push('OIDC Authority が設定されていません');
  }

  if (!config.oidc.clientId) {
    errors.push('OIDC Client ID が設定されていません');
  }

  if (!config.apiBaseUrl) {
    errors.push('API Base URL が設定されていません');
  }

  return errors;
}
